<template>
    <div>
        <div class="pb-3">
            <span class="text-subtitle-2">{{ $t('loyalty_points') }}: </span>
            <span class="text-subtitle-1 font-weight-bold">{{ totalCount }}</span>
        </div>
        <loyalty-points-filters
            v-model="filters"
            :has-active-filters="hasActiveFilters"
            @submit="applyFilters"
            @reset="resetFilters"
        />
        <v-card variant="outlined">
            <v-data-table-server
                :headers="headers"
                :items="loyaltyPoints"
                :items-per-page-options="[10, 15, 50, -1]"
                :loading="isLoadingData"
                :options="options"
                :items-length="count"
                disable-sort
                class="elevation-1 table-server"
                data-cy="loyalty-points-table"
                density="compact"
                color="primary"
                @update:options="onUpdateTableOptions"
            >
                <template v-slot:item.referenceNumber="{item}">
                    <nuxt-link
                        no-prefetch
                        :to="{name: 'order-details', params: {id: item.referenceNumber}}"
                        :data-cy="`order-link-${item.referenceNumber}`"
                    >
                        <strong>{{ item.referenceNumber }}</strong>
                    </nuxt-link>
                </template>
                <template v-slot:item.serviceProcess="{item}">
                    <div v-if="item.serviceName" class="text-primary text-subtitle-2">{{ item.serviceName }}</div>
                    <span
                        v-if="item.serviceProcess.cityName || item.serviceProcess.countryName"
                        class="text-grey text-caption"
                    >
                        {{ formattedLocation(item.serviceProcess) }}
                    </span>
                    <div v-if="item.serviceProcess.startDate || item.serviceProcess.endDate">
                        {{ $filters.dateShortFormat(item.serviceProcess.startDate) }} -
                        {{ $filters.dateShortFormat(item.serviceProcess.endDate) }}
                    </div>
                </template>
                <template v-slot:item.processStatus="{item}">
                    {{ item.processStatus ? $t(`bookingStatus.${item.processStatus}`) : '' }}
                </template>
                <template v-slot:item.bruttoPriceValue="{item}">
                    <div v-if="item.bruttoPriceCurrency && item.bruttoPriceValue">
                        {{ $filters.priceFormat(item.bruttoPriceValue, item.bruttoPriceCurrency) }}
                    </div>
                    <div v-else>{{ item.bruttoPriceCurrency }} {{ item.bruttoPriceValue || '' }}</div>
                </template>
                <template v-slot:item.lastModificationTime="{item}">
                    {{ item.lastModificationTime ? $filters.dateTimeFormat(item.lastModificationTime) : '' }}
                </template>
                <template v-slot:item.transactionStatus="{item}">
                    <span :class="getTransactionStatusClass(item.transactionStatus)">
                        {{ item.transactionStatus ? $t(`transaction_statuses.${item.transactionStatus}`) : '' }}
                    </span>
                </template>
            </v-data-table-server>
        </v-card>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {clean, clone, formatDateToISO, isEqual} from '@/utils/helpers'
    import {LoyaltyPointsFilters} from '~/components/account/clients/LoyaltyPointsFilters.vue'
    import {
        type LoyaltyPointsFilterType,
        type LoyaltyPointType,
        TransactionStatus,
    } from '~/utils/types/account/clients/types'

    const initFilters = (): LoyaltyPointsFilterType => ({
        transactionStatus: null,
        rangeType: null,
        dateFrom: null,
        dateTo: null,
    })

    @Component({
        components: {LoyaltyPointsFilters},
    })
    export class LoyaltyPoints extends Vue {
        @Prop({required: true, type: Number}) clientId: number

        options = {
            itemsPerPage: 10,
            page: 1,
        }
        filters: LoyaltyPointsFilterType = initFilters()
        loyaltyPoints: LoyaltyPointType[] = []
        totalCount: number = 0
        count: number = 0
        isLoadingData: boolean = false

        applyFilters(): void {
            this.initOptions()
            this.loadLoyaltyPoints()
        }

        resetFilters(): void {
            this.filters = initFilters()
            this.initOptions()
            this.loadLoyaltyPoints()
        }

        get hasActiveFilters(): boolean {
            return !isEqual(initFilters(), this.filters)
        }

        async loadLoyaltyPoints(): Promise<void> {
            try {
                this.isLoadingData = true
                const filters = clone(this.filters)
                delete filters.rangeType
                clean(filters)
                if (filters.dateFrom) filters.dateFrom = formatDateToISO(filters.dateFrom)
                if (filters.dateTo) filters.dateTo = formatDateToISO(filters.dateTo)

                const {itemsPerPage, page} = this.options
                const res = await this.$api.loyaltyPoints.get(this.clientId, {
                    ...filters,
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                })

                this.loyaltyPoints = res.loyaltyPoints.map(item =>
                    Object.assign(item, {processStatus: item.processStatus.toUpperCase().replace(/\s+/g, '_')})
                )

                this.totalCount = res.totalPoints
                this.count = res.count
            } catch (e) {
                this.loyaltyPoints = []
                this.totalCount = 0
                this.count = 0
            } finally {
                this.isLoadingData = false
            }
        }

        initOptions(): void {
            this.options = {page: 1, itemsPerPage: this.options.itemsPerPage}
        }

        onUpdateTableOptions(options): void {
            this.options = options
            this.loadLoyaltyPoints()
        }

        get headers(): any[] {
            return [
                {title: `${this.$t('order_number')}`, value: 'referenceNumber', width: '10%'},
                {title: this.$t('order_service'), value: 'serviceProcess'},
                {title: this.$t('status'), value: 'processStatus'},
                {title: this.$t('price'), value: 'bruttoPriceValue', align: 'end'},
                {title: this.$t('loyalty_points'), value: 'loyaltyPoints', align: 'end'},
                {title: this.$t('date_time'), value: 'lastModificationTime'},
                {title: this.$t('transaction_status'), value: 'transactionStatus', width: '10%'},
            ]
        }

        getTransactionStatusClass(status): {[key: string]: boolean} {
            return {
                'text-red': status === TransactionStatus.Completed,
                'text-green': status === TransactionStatus.Pending,
            }
        }

        formattedLocation(service): string {
            const {countryName, cityName} = service
            return `${countryName || ''}${countryName && cityName ? ',' : ''} ${cityName || ''}`
        }
    }

    export default LoyaltyPoints
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(div) {
        td:nth-of-type(6) {
            white-space: nowrap;
        }
    }
</style>
